import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { AuthProvider } from "@/contexts/AuthContext";
import { AUTH_DISABLED, NEWS_ENABLED } from "@/lib/featureFlags";
import { AnalyticsTracker } from "@/components/AnalyticsTracker";
import Index from "./pages/Index";

// Everything except the landing page loads on demand, so a first visit does not
// download the admin panel, the image editor (Fabric.js) or chart code.
const Admin = lazy(() => import("./pages/Admin"));
const Compare = lazy(() => import("./pages/Compare"));
const Live = lazy(() => import("./pages/Live"));
const LiveTV = lazy(() => import("./pages/LiveTV"));
const Manager = lazy(() => import("./pages/Manager"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Contribute = lazy(() => import("./pages/Contribute"));
const ApproveResult = lazy(() => import("./pages/ApproveResult"));
const Settings = lazy(() => import("./pages/Settings"));
const NotFound = lazy(() => import("./pages/NotFound"));
const News = lazy(() => import("./pages/News"));
const Rankings = lazy(() => import("./pages/Rankings"));
const Create = lazy(() => import("./pages/Create"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Coverage = lazy(() => import("./pages/Coverage"));

const Terms = lazy(() => import("./pages/Terms"));
const SackRace = lazy(() => import("./pages/SackRace"));
const PressureIndex = lazy(() => import("./pages/PressureIndex"));
const LeagueHub = lazy(() => import("./pages/LeagueHub"));
const ClubPage = lazy(() => import("./pages/ClubPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center">
    <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AnalyticsTracker />
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/manager/:slug" element={<Manager />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/live" element={<Live />} />
                <Route path="/live/tv" element={<LiveTV />} />
                <Route path="/live/:date" element={<Live />} />
                {!AUTH_DISABLED && (
                  <>
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/contribute" element={<Contribute />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/create" element={<Create />} />
                    <Route path="/approve-result" element={<ApproveResult />} />
                    <Route path="/pricing" element={<Pricing />} />
                  </>
                )}

                {NEWS_ENABLED && <Route path="/news" element={<News />} />}
                <Route path="/rankings" element={<Rankings />} />
                <Route path="/sack-race" element={<SackRace />} />
                <Route path="/pressure-index" element={<PressureIndex />} />
                <Route path="/league/:code" element={<LeagueHub />} />
                <Route path="/club/:slug" element={<ClubPage />} />
                <Route path="/coverage" element={<Coverage />} />
                <Route path="/privacy" element={<Privacy />} />

                <Route path="/terms" element={<Terms />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
